import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    ThreadStatus,
    ThreadType,
    ThreadBotStatus,
    ContactFilter,
} from 'nutshell-graphql-types';
import type {
    GetLatestThreads as GetLatestThreadsQuery,
    GetLatestThreadsVariables as GetLatestThreadsQueryVariables,
    InboxThreadFragment,
} from 'nutshell-graphql-types';

import GET_LATEST_THREADS from '../queries/get-latest-threads.graphql';

const LIMIT = 20;
const POLL_INTERVAL_MILLISECONDS = 5000; // 5 seconds

export const useGetLatestThreads = (input: {
    status: ThreadStatus;
    assignees: string[];
    threadType: ThreadType | null | undefined;
    findThreadId?: string;
    botStatus?: ThreadBotStatus;
    contactFilter?: ContactFilter;
    search?: string;
}) => {
    // Track previous status to detect status changes
    const previousStatus = React.useRef(input.status);
    const isStatusChange = previousStatus.current !== input.status;

    const previousAssignees = React.useRef(input.assignees);
    const isAssigneesChange = previousAssignees.current !== input.assignees;

    const previousSearch = React.useRef(input.search);
    const isSearchChange = previousSearch.current !== input.search;

    const {data, error, fetchMore, refetch, networkStatus} = useQuery<
        GetLatestThreadsQuery,
        GetLatestThreadsQueryVariables
    >(GET_LATEST_THREADS, {
        variables: {...input, first: LIMIT},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    // Show loading only for initial load or status/search changes
    const isLoading =
        networkStatus === 1 ||
        (isStatusChange && networkStatus !== 7) ||
        (isAssigneesChange && networkStatus !== 7) ||
        (isSearchChange && networkStatus !== 7);

    // Update previous values after checking for changes
    React.useEffect(() => {
        // Only update when we're done loading
        if (networkStatus === 7) {
            previousStatus.current = input.status;
            previousAssignees.current = input.assignees;
            previousSearch.current = input.search;
        }
    }, [input.status, input.assignees, input.search, networkStatus]);

    const threadsData = data ? data.latestThreads : undefined;

    const latestThreads: InboxThreadFragment[] = threadsData
        ? threadsData.edges.map((edge) => edge.node)
        : [];

    const totalCount = threadsData ? threadsData.totalCount : 0;
    const hasNextPage = threadsData ? threadsData.pageInfo.hasNextPage : false;

    const beforeCursor = threadsData ? threadsData.pageInfo.startCursor : null;
    const endCursor = threadsData ? threadsData.pageInfo.endCursor : null;

    React.useEffect(() => {
        // Pause polling while search is active to avoid result flickering
        if (input.search) {
            return;
        }

        const tick = () => {
            fetchMostRecentThreads(beforeCursor);
        };

        const id = setInterval(tick, POLL_INTERVAL_MILLISECONDS);

        return () => clearInterval(id);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [beforeCursor, input.search]);

    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: LIMIT,
                after: endCursor,
                findThreadId: null,
            },
            updateQuery: (prev, {fetchMoreResult}) => {
                if (!fetchMoreResult) {
                    return prev;
                }

                const oldThreads = prev.latestThreads;
                const oldEdges = oldThreads ? oldThreads.edges : [];
                const newThreads = fetchMoreResult.latestThreads;
                const newEdges = newThreads ? newThreads.edges : [];

                // Dedupe threads by ID
                const existingIds = new Set(oldEdges.map((edge) => edge.node.id));
                const uniqueNewEdges = newEdges.filter((edge) => !existingIds.has(edge.node.id));

                const prevEndCursor = oldThreads.pageInfo.endCursor;
                const newPageInfo = newThreads.pageInfo;
                const newHasNextPage = newPageInfo ? newPageInfo.hasNextPage : false;
                const newEndCursor = newPageInfo.endCursor || prevEndCursor;

                return {
                    latestThreads: {
                        ...fetchMoreResult.latestThreads,
                        edges: oldEdges.concat(uniqueNewEdges),
                        pageInfo: {
                            ...oldThreads.pageInfo,
                            endCursor: newEndCursor,
                            hasNextPage: newHasNextPage,
                        },
                    },
                };
            },
        });
    };

    // Called when polling - gets any new events and puts them at the bottom of the threads list
    const fetchMostRecentThreads = (cursor?: string | null) => {
        if (!cursor && totalCount !== 0) {
            // no-op if no beforeCursor is passed
            return new Promise((resolve) => {
                // @ts-expect-error
                resolve();
            });
        }

        // @ts-expect-error
        return fetchMore({
            variables: {
                first: undefined, // must be undefined to get the most recent events without erroring
                last: LIMIT,
                before: cursor,
                findThreadId: null,
            },
            updateQuery: (prev, {fetchMoreResult}) => {
                const oldThreads = prev ? prev.latestThreads : {};
                // @ts-expect-error
                const oldEdges = oldThreads && oldThreads.edges ? oldThreads.edges : [];
                // @ts-expect-error
                const mostRecentThreads = fetchMoreResult.latestThreads;
                const mostRecentEdges = mostRecentThreads.edges;
                if (mostRecentEdges.length === 0) {
                    return prev;
                }

                const newIds = new Set(mostRecentEdges.map((edge) => edge.node.id));
                // @ts-expect-error
                const uniqueOldEdges = oldEdges.filter((edge) => !newIds.has(edge.node.id));

                // @ts-expect-error
                const prevStartCursor = oldThreads.pageInfo.startCursor;
                // @ts-expect-error
                const prevEndCursor = oldThreads.pageInfo.endCursor;

                const newStartCursor = mostRecentThreads.pageInfo.startCursor || prevStartCursor;
                const newEndCursor = prevEndCursor || mostRecentThreads.pageInfo.endCursor;

                const newThreads = {
                    ...oldThreads,
                    edges: mostRecentEdges.concat(uniqueOldEdges),
                    pageInfo: {
                        // @ts-expect-error
                        ...oldThreads.pageInfo,
                        startCursor: newStartCursor,
                        endCursor: newEndCursor,
                    },
                } as const;

                return {latestThreads: newThreads};
            },
        });
    };

    return {
        latestThreads,
        totalCount,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        hasNextPage,
        fetchMoreRows,
        refetch,
    };
};
